import { TranslocoService } from '@jsverse/transloco';
import { Observable, of } from 'rxjs';
import { catchError, map, switchMap, take } from 'rxjs/operators';
import { RestService } from '../../../../shared/services/rest.service';
import { ToastService } from '../../../../shared/services/toast.service';
import { UserRepository } from '../../../user/states/user.repository';

/**
 * Gemeinsame Basis der medizinischen Ressourcen mit GET/POST/PUT/DELETE.
 * Fehler werden wie im `AnimalNoteRepository` als Toast gemeldet und als `null`/`false`
 * zurückgegeben; `silent` unterdrückt den Toast, wenn der Aufrufer selbst zusammenfasst.
 */
export abstract class AnimalMedicalCrudRepository<TModel, TCreate extends { animal: { id: number } }, TUpdate> {
  protected abstract readonly path: string;
  /** i18n-Präfix unter `animalMedical.toast`, z. B. `treatment`. */
  protected abstract readonly toastKey: string;
  /** Ob beim Anlegen der eingeloggte Benutzer als `creator` mitgesendet wird. */
  protected readonly withCreator: boolean = true;

  protected constructor(
    protected readonly _restService: RestService,
    protected readonly _toast: ToastService,
    protected readonly _translateService: TranslocoService,
    protected readonly _userRepository: UserRepository
  ) {}

  getByAnimalId(animalId: number): Observable<TModel[]> {
    return this._restService.httpGet(`${this.path}?pagination=false&animal.id=${animalId}`).pipe(
      take(1),
      map(response => (Array.isArray(response) ? response as TModel[] : [])),
      catchError(() => {
        this._toast.showError(this._translateService.translate('animalMedical.toast.loadError'));
        return of([] as TModel[]);
      })
    );
  }

  create(payload: TCreate, silent = false): Observable<TModel | null> {
    const body$ = this.withCreator
      ? this._userRepository.getActiveUser().pipe(
          take(1),
          map(activeUser => {
            if (!activeUser?.id) throw new Error('Kein aktiver Benutzer');
            return { ...payload, creator: { id: activeUser.id } };
          })
        )
      : of(payload);

    return body$.pipe(
      switchMap(body => this._restService.httpPost(this.path, body).pipe(take(1))),
      map(response => this.ensureResponse<TModel>(response)),
      catchError(() => this.fail<TModel>('saveError', silent))
    );
  }

  update(id: number, payload: TUpdate, silent = false): Observable<TModel | null> {
    return this._restService.httpPut(`${this.path}/${id}`, payload).pipe(
      take(1),
      map(response => this.ensureResponse<TModel>(response)),
      catchError(() => this.fail<TModel>('saveError', silent))
    );
  }

  delete(id: number, silent = false): Observable<boolean> {
    return this._restService.httpDelete(`${this.path}/${id}`).pipe(
      take(1),
      map(() => true),
      catchError(() => {
        if (!silent) {
          this._toast.showError(this._translateService.translate(`animalMedical.toast.${this.toastKey}.deleteError`));
        }
        return of(false);
      })
    );
  }

  /** `RestService` liefert ohne gültiges Token `null` statt eines Fehlers. */
  protected ensureResponse<T>(response: unknown): T {
    if (response === null || response === undefined) {
      throw new Error('Leere Antwort');
    }
    return response as T;
  }

  protected fail<T>(key: 'saveError', silent: boolean): Observable<T | null> {
    if (!silent) {
      this._toast.showError(this._translateService.translate(`animalMedical.toast.${this.toastKey}.${key}`));
    }
    return of(null);
  }
}
